import { combineReducers } from 'redux';
import queryString from 'query-string';
import { takeLatest, takeEvery, call, select, put } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import request from '../api-utils/request';
import actionKey from '../utils/action-key';
import { REMOVE_USER_FROM_COMPANY, EDIT_USER } from './admin';
import { UPDATE_USER_POSITION } from './manage-position';

export const SEARCH_USERS = 'users-search/SEARCH_USERS';
export const SELECT_USER_TO_MOVE = 'users-search/SELECT_USER_TO_MOVE';
export const CHANGE_FILTER = 'users-search/CHANGE_FILTER';
export const DEFAULT_FILTER = 'users-search/DEFAULT_FILTER';
export const HAVE_ARCHIVED_CHECK = 'users-search/HAVE_ARCHIVED_CHECK';

export const usersList = (state = {}, action) => {
    switch (action.type) {
        case actionKey.success(SEARCH_USERS): // eslint-disable-line no-case-declarations
            const arr = Object.values(state);
            const newSate = arr.reduce((acc, user) => {
                acc[user.id] = { // eslint-disable-line no-param-reassign
                    ...(state[user.id] || {}),
                    ...user,
                    moveSelected: false,
                    name: `${user.firstName} ${user.lastName}`,
                };
                return acc;
            }, {});
            return {
                ...newSate,
                ...action.payload.users.reduce((acc, user) => {
                    acc[user.id] = { // eslint-disable-line no-param-reassign
                        ...(state[user.id] || {}),
                        ...user,
                        moveSelected: false,
                        name: `${user.firstName} ${user.lastName}`,
                    };
                    return acc;
                }, {}),
            };
        case SELECT_USER_TO_MOVE: {
            const user = action.payload.user;
            return {
                ...state,
                [user.id]: {
                    ...user,
                    moveSelected: !user.moveSelected,
                },
            };
        }
        default:
            return state;
    }
};

export const selectUserToMoveAction = payload => ({
    type: SELECT_USER_TO_MOVE,
    payload,
});

export const maxReached = (state = false, action) => {
    switch (action.type) {
        case SEARCH_USERS:
            return false;
        case actionKey.success(SEARCH_USERS):
            return action.payload.users.length < 20;
        default:
            return state;
    }
};

export const searchList = (state = [], action) => {
    switch (action.type) {
        case DEFAULT_FILTER:
            return [];
        case actionKey.init(SEARCH_USERS): {
            if (action.offset !== 0) {
                return state;
            }
            return [];
        }
        case actionKey.success(SEARCH_USERS):
            return [
                ...state,
                ...action.payload.users.map(({ id }) => id),
            ];
        default:
            return state;
    }
};

export const loading = (state = null, action) => {
    switch (action.type) {
        case CHANGE_FILTER:
        case SEARCH_USERS:
            return true;
        case actionKey.success(SEARCH_USERS):
        case actionKey.failure(SEARCH_USERS):
            return false;
        default:
            return state;
    }
};

export const error = (state = null, action) => {
    switch (action.type) {
        case SEARCH_USERS:
        case actionKey.success(SEARCH_USERS):
            return null;
        case actionKey.failure(SEARCH_USERS):
            return action.error;
        default:
            return state;
    }
};

export const filter = (state = {}, action) => {
    switch (action.type) {
        case DEFAULT_FILTER:
            return {
                ...action.payload.initialFilter,
            };
        case CHANGE_FILTER: {
            const newState = {
                ...state,
                ...action.payload,
            };
            newState.offset = action.payload.offset || 0;
            if ('query' in action.payload) {
                newState.sortBy = undefined;
            }
            if (action.payload.status === 'in-progress') {
                newState.sortBy = 'assessmentsCompleted';
                newState.order = 'DESC';
            }

            if (action.payload.status === 'completed') {
                newState.order = 'DESC';
                newState.sortBy = 'completedAt';
            }
            return newState;
        }
        case actionKey.init(SEARCH_USERS): {
            if (typeof action.query === 'undefined') {
                return {
                    ...state,
                    query: '',
                };
            }
            return state;
        }
        default:
            return state;
    }
};

export const empty = (state = false, action) => {
    switch (action.type) {
        case SEARCH_USERS:
            return false;
        case actionKey.success(SEARCH_USERS):
            return !action.query &&
                (!action.payload.users || action.payload.users.length === 0) &&
                (!action.offset || action.offset === 0);
        default:
            return state;
    }
};

export const initialLoading = (state = false, action) => {
    switch (action.type) {
        case DEFAULT_FILTER:
            return true;
        case actionKey.init(SEARCH_USERS):
            return typeof action.query === 'undefined';
        case actionKey.success(SEARCH_USERS):
        case actionKey.failure(SEARCH_USERS):
            return false;
        default:
            return state;
    }
};

export const haveArchived = (state = false, action) => {
    switch (action.type) {
        case actionKey.success(HAVE_ARCHIVED_CHECK):
            return action.payload.archived;
        default:
            return state;
    }
};

export default combineReducers({
    error,
    loading,
    list: usersList,
    search: searchList,
    filter,
    initialLoading,
    empty,
    maxReached,
    haveArchived,
});

/**
 * REDUX ACTIONS
 */

export const searchFitScoresAction = () => ({
    type: SEARCH_USERS,
});


export const changeFilterAction = payload => ({
    type: CHANGE_FILTER,
    payload,
});


export const defaultFilterAction = payload => ({
    type: DEFAULT_FILTER,
    payload,
});

export const haveArchivedCheckAction = () => ({
    type: HAVE_ARCHIVED_CHECK,
});

/**
 * SAGA LISTENERS
 */

export function* rootSaga() {
    yield [
        takeLatest(SEARCH_USERS, searchFitScores),
        takeLatest(CHANGE_FILTER, searchFitScoresOnChange),
        takeEvery(DEFAULT_FILTER, defaultFilterTrigger),
        takeEvery([HAVE_ARCHIVED_CHECK, UPDATE_USER_POSITION], haveArchivedCheck),
        takeEvery([
            actionKey.success(REMOVE_USER_FROM_COMPANY),
            actionKey.success(EDIT_USER),
        ], refreshFitScoresSearch),
    ];
}

export function* refreshFitScoresSearch() {
    yield put(changeFilterAction({ offset: 0 }));
    yield triggerFitScoresSearch();
}

export function* triggerFitScoresSearch() {
    yield put(searchFitScoresAction());
}

export function* defaultFilterTrigger({ payload }) {
    if (payload.trigger) {
        yield triggerFitScoresSearch();
    }
}

export function* searchFitScoresOnChange(action) {
    if (action.payload.query) {
        yield call(delay, 350);
    }
    yield triggerFitScoresSearch();
}
/**
 * REDUX SAGAS
 */
export function* searchFitScores() {
    const { usersSearch } = yield select();
    const params = queryString.stringify(usersSearch.filter);
    yield call(request, {
        url: `/api/users/search?${params}`,
        type: SEARCH_USERS,
        ...usersSearch.filter,
    });
}


export function* haveArchivedCheck() {
    yield call(request, {
        url: '/api/users/have-archived',
        type: HAVE_ARCHIVED_CHECK,
    });
}



// WEBPACK FOOTER //
// ./src/client/redux/users-search.js